import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from './AuthContext';
import { toast } from "sonner";
import { generateDefaultWidgets } from "../lib/widgetUtils";
import { GridItemLayout, WidgetData, WidgetType } from "../types";
import { GetCurrentDashboard, GetDashboards, Dashboard } from "../services/DashboardService";
import { clearAllAppCookies } from "@/lib/cookies";

type Theme = "light" | "dark";

interface TabData {
  widgets: WidgetData[];
  layouts: GridItemLayout[];
}

interface DashboardLayoutType {
  id: string;
  name: string;
  tabs: {
    'key-metrics': TabData;
    'call-overview': TabData;
    'agent-performance': TabData;
    'trends-insights': TabData;
  };
}

interface DashboardContextType {
  layouts: DashboardLayoutType[];
  currentLayout: DashboardLayoutType;
  updateLayout: (newLayout: GridItemLayout[]) => void;
  addWidget: (widgetType: WidgetType, title?: string) => void;
  removeWidget: (widgetId: string) => void;
  updateWidget: (widgetId: string, widgetData: Partial<WidgetData>) => void;
  updateWidgetTemp: (widgetId: string, widgetData: Partial<WidgetData>) => void;
  resetWidgetTemp: (widgetId: string, originalWidget: WidgetData) => void;
  saveDashboard: () => void;
  resetDashboard: () => void;
  openWidgetSettings: (widgetId: string) => void;
  closeWidgetSettings: () => void;
  currentWidgetSettings: WidgetData | null;
  createNewLayout: (name: string) => void;
  switchLayout: (layoutId: string) => void;
  deleteLayout: (layoutId: string) => void;
  theme: Theme;
  setTheme: (theme: Theme) => void;
  isGeneralMode: boolean;
  toggleGeneralMode: () => void;
  setIsGeneralMode: (val: boolean) => void;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  // Added dashboard data (merged from DashboardDataContext)
  current: Dashboard | null;
  setCurrent: (id: number) => void;
}

const defaultLayout: DashboardLayoutType = {
  id: "general",
  name: "General Layout",
  tabs: {
    'key-metrics': {
      widgets: generateDefaultWidgets('key-metrics'),
      layouts: generateDefaultWidgets('key-metrics').map(widget => widget.layout as GridItemLayout),
    },
    'call-overview': {
      widgets: generateDefaultWidgets('call-overview'),
      layouts: generateDefaultWidgets('call-overview').map(widget => widget.layout as GridItemLayout),
    },
    'agent-performance': {
      widgets: generateDefaultWidgets('agent-performance'),
      layouts: generateDefaultWidgets('agent-performance').map(widget => widget.layout as GridItemLayout),
    },
    'trends-insights': {
      widgets: generateDefaultWidgets('trends-insights'),
      layouts: generateDefaultWidgets('trends-insights').map(widget => widget.layout as GridItemLayout),
    }
  }
};

export const DashboardContext = createContext<DashboardContextType>({
  layouts: [defaultLayout],
  currentLayout: defaultLayout,
  updateLayout: () => {},
  addWidget: () => {},
  removeWidget: () => {},
  updateWidget: () => {},
  updateWidgetTemp: () => {},
  resetWidgetTemp: () => {},
  saveDashboard: () => {},
  resetDashboard: () => {},
  openWidgetSettings: () => {},
  closeWidgetSettings: () => {},
  currentWidgetSettings: null,
  createNewLayout: () => {},
  switchLayout: () => {},
  deleteLayout: () => {},
  theme: "light",
  setTheme: () => {},
  isGeneralMode: false,
  toggleGeneralMode: () => {},
  setIsGeneralMode: () => {},
  activeTab: "key-metrics",
  setActiveTab: () => {},
  current: null,
  setCurrent: () => {},
});

export const useDashboard = () => useContext(DashboardContext);

interface DashboardProviderProps {
  children: React.ReactNode;
  tab?: string;
}

// Hàm fetchWidgetData dùng chung cho addWidget và General
export async function fetchWidgetData(widgetType: WidgetType): Promise<Partial<WidgetData>> {
  try {
    // Thay endpoint này bằng API thật khi có
    const res = await fetch(`/api/widget-data?type=${widgetType}`);
    if (!res.ok) throw new Error('API error');
    const data = await res.json();
    return data;
  } catch (e) {
    // Nếu lỗi, trả về mockup data
    switch (widgetType) {
      case WidgetType.CALL_VOLUMES:
        return { value: Math.floor(Math.random() * 2000), subtitle: "current month", description: "Total number of calls in the selected period" };
      case WidgetType.CALL_WAIT_TIME:
        return { value: `${Math.floor(Math.random() * 3)}:${Math.floor(Math.random() * 60).toString().padStart(2, "0")}`, subtitle: "average in seconds", description: "Average time callers wait before being connected" };
      case WidgetType.AGENT_AVAILABILITY:
        return { value: `${Math.floor(Math.random() * 100)}%`, subtitle: "current status", description: "Percentage of time agents are available" };
      case WidgetType.CALLS_IN_QUEUE:
        return { value: Math.floor(Math.random() * 30), subtitle: "waiting now", description: "Current number of calls waiting to be answered" };
      case WidgetType.AGENT_DAILY_CALLS:
        return { value: Math.floor(Math.random() * 100), subtitle: "per agent", description: "Average number of calls handled per agent daily" };
      case WidgetType.AGENT_PRESENCE:
        return { value: `${Math.floor(Math.random() * 100)}%`, subtitle: "online now", description: "Summary of agent status across the system" };
      case WidgetType.AGENT_PERFORMANCE:
        return { value: Math.floor(Math.random() * 100), subtitle: "performance score", description: "Key performance metrics for agents" };
      case WidgetType.AGENT_STATUS:
        return { value: "Active", subtitle: "current status", description: "Current status of all agents" };
      default:
        return { value: 0, subtitle: "", description: "Widget description" };
    }
  }
}

export const DashboardProvider: React.FC<DashboardProviderProps> = ({
  children,
  tab = 'key-metrics',
}) => {
  const [layouts, setLayouts] = useState<DashboardLayoutType[]>(() => {
    // const savedLayouts = localStorage.getItem('dashboardLayouts');
    // if (savedLayouts) {
    //   return JSON.parse(savedLayouts);
    // }
    return [defaultLayout];
  });

  const [currentLayout, setCurrentLayout] = useState<DashboardLayoutType>(layouts[0]);
  const [currentWidgetSettings, setCurrentWidgetSettings] = useState<WidgetData | null>(null);
  const [theme, setTheme] = useState<Theme>("light");
  const [isGeneralMode, setIsGeneralMode] = useState(true);
  const [activeTab, setActiveTab] = useState<string>("key-metrics");
  // Store temporary widget values (not saved to browser cache)
  const [tempWidgetValues, setTempWidgetValues] = useState<Record<string, WidgetData>>({});
  // Dashboard data state (merged)
  const [current, setCurrentState] = useState<Dashboard | null>(GetCurrentDashboard());
  // Auth context (used for token refresh after hard refresh)
  const { microsoftToken, getGromToken, user } = useAuth();

  const toggleGeneralMode = () => {
    setIsGeneralMode(prev => !prev);
  };

  // Load layouts and theme from localStorage on initial render
  useEffect(() => {
    // const savedLayouts = localStorage.getItem('dashboardLayouts');
    const savedTheme = localStorage.getItem("dashboardTheme") as Theme;

    if (savedTheme) {
      setTheme(savedTheme);
      document.documentElement.classList.toggle("dark", savedTheme === "dark");
    }

    // if (savedLayouts) {
    //   try {
    //     const parsedLayouts = JSON.parse(savedLayouts);
    //     setLayouts(parsedLayouts);
    //     setCurrentLayout(parsedLayouts[0]);
    //   } catch (error) {
    //     console.error("Error loading dashboard layouts from localStorage:", error);
    //     setLayouts([defaultLayout]);
    //     setCurrentLayout(defaultLayout);
    //   }
    // }
  }, []);

  useEffect(() => {
    const handleDashboardChanged = async () => {
      try {
        clearAllAppCookies();
        localStorage.setItem('dashboardTheme', theme);
        if (microsoftToken) {
          try {
            await getGromToken(microsoftToken);
          } catch (err) {
            console.warn('[DashboardProvider] getGromToken failed during hard refresh', err);
          }
        }
      } catch (e) {
        console.error('[DashboardProvider] Hard refresh failed, forcing reload', e);
        console.log('[DashboardProvider] Reloading tab: dashboardChanged handler failed (clearAllAppCookies/getGromToken)');
        localStorage.clear();
        window.location.reload();
      }
    };
    window.addEventListener('dashboardChanged', handleDashboardChanged);
    return () => window.removeEventListener('dashboardChanged', handleDashboardChanged);
  }, [layouts, theme, microsoftToken, getGromToken, user]);

  // Update theme in localStorage and DOM when it changes
  useEffect(() => {
    localStorage.setItem("dashboardTheme", theme);
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  // Save layouts to localStorage when they change
  // useEffect(() => {
  //   localStorage.setItem('dashboardLayouts', JSON.stringify(layouts));
  // }, [layouts]);

  const updateLayout = (newLayouts: GridItemLayout[]) => {
    setCurrentLayout(prev => {
      const updatedTabs = { ...prev.tabs };
      const currentTab = activeTab as keyof typeof updatedTabs;

      const updatedWidgets = updatedTabs[currentTab].widgets.map(widget => {
        const newLayout = newLayouts.find(layout => layout.i === widget.id);
        if (newLayout) {
          return { ...widget, layout: newLayout };
        }
        return widget;
      });

      updatedTabs[currentTab] = {
        widgets: updatedWidgets,
        layouts: newLayouts,
      };

      const updatedLayout = {
        ...prev,
        tabs: updatedTabs,
      };

      setLayouts(prevLayouts =>
        prevLayouts.map(layout =>
          layout.id === updatedLayout.id ? updatedLayout : layout
        )
      );

      return updatedLayout;
    });
  };

  const addWidget = async (widgetType: WidgetType, title?: string) => {
    const widgetId = `widget-${Date.now()}`;
    const currentTab = activeTab as keyof typeof currentLayout.tabs;
    const lastY = currentLayout.tabs[currentTab].layouts.length
      ? Math.max(...currentLayout.tabs[currentTab].layouts.map(l => l.y + (l.h || 1)))
      : 0;

    const newLayout: GridItemLayout = {
      i: widgetId,
      x: 0,
      y: lastY,
      w: 4,
      h: 2,
      minW: 2,
      minH: 2,
    };

    const apiData = await fetchWidgetData(widgetType);

  const widgetData: WidgetData = {
      id: widgetId,
      type: widgetType,
      title: title || "New Widget",
      value: apiData.value ?? 0,
      subtitle: apiData.subtitle,
      description: apiData.description,
      layout: newLayout,
    };

    if (apiData.thresholds) widgetData.thresholds = apiData.thresholds;
    if (apiData.maxValue) widgetData.maxValue = apiData.maxValue;

    setCurrentLayout(prev => {
      const updatedTabs = { ...prev.tabs };
      updatedTabs[currentTab] = {
        widgets: [...updatedTabs[currentTab].widgets, widgetData],
        layouts: [...updatedTabs[currentTab].layouts, newLayout],
      };

      const updatedLayout = {
        ...prev,
        tabs: updatedTabs,
      };

      setLayouts(prevLayouts =>
        prevLayouts.map(layout =>
          layout.id === updatedLayout.id ? updatedLayout : layout
        )
      );

      return updatedLayout;
    });
  };

  const removeWidget = (widgetId: string) => {
    setCurrentLayout(prev => {
      const updatedTabs = { ...prev.tabs };
      const currentTab = activeTab as keyof typeof updatedTabs;

      updatedTabs[currentTab] = {
        widgets: updatedTabs[currentTab].widgets.filter(w => w.id !== widgetId),
        layouts: updatedTabs[currentTab].layouts.filter(l => l.i !== widgetId),
      };

      const updatedLayout = {
        ...prev,
        tabs: updatedTabs,
      };

      setLayouts(prevLayouts =>
        prevLayouts.map(layout =>
          layout.id === updatedLayout.id ? updatedLayout : layout
        )
      );

      return updatedLayout;
    });

    // If we're removing the widget that's currently being edited, close the settings
    if (currentWidgetSettings && currentWidgetSettings.id === widgetId) {
      setCurrentWidgetSettings(null);
    }

    // Also remove from temp values if it exists
    if (tempWidgetValues[widgetId]) {
      setTempWidgetValues(prev => {
        const newValues = { ...prev };
        delete newValues[widgetId];
        return newValues;
      });
    }

    toast.success("Widget removed successfully");
  };

  // Update widget in the actual saved layout (browser cache)
  const updateWidget = (widgetId: string, widgetData: Partial<WidgetData>) => {
    setCurrentLayout(prev => {
      const updatedTabs = { ...prev.tabs };
      const currentTab = activeTab as keyof typeof updatedTabs;

      updatedTabs[currentTab] = {
        ...updatedTabs[currentTab],
        widgets: updatedTabs[currentTab].widgets.map(widget =>
          widget.id === widgetId ? { ...widget, ...widgetData } : widget
        ),
      };

      const updatedLayout = {
        ...prev,
        tabs: updatedTabs,
      };

      setLayouts(prevLayouts =>
        prevLayouts.map(layout =>
          layout.id === updatedLayout.id ? updatedLayout : layout
        )
      );

      return updatedLayout;
    });
  };

  // Update widget temporarily (not saved to browser cache)
  const updateWidgetTemp = (widgetId: string, widgetData: Partial<WidgetData>) => {
    setTempWidgetValues(prev => ({
      ...prev,
      [widgetId]: {
        ...prev[widgetId],
        ...widgetData,
      },
    }));
  };

  // Reset a widget to its original state
  const resetWidgetTemp = (widgetId: string, originalWidget: WidgetData) => {
    setTempWidgetValues(prev => {
      const newValues = { ...prev };
      delete newValues[widgetId];
      return newValues;
    });
  };

  const saveDashboard = () => {
    setLayouts(prevLayouts =>
      prevLayouts.map(layout =>
        layout.id === currentLayout.id ? currentLayout : layout
      )
    );
  };

  const resetDashboard = () => {
    setCurrentLayout(defaultLayout);
    setLayouts([defaultLayout]);
  };

  const createNewLayout = (name: string) => {
    const newLayout: DashboardLayoutType = {
      id: `layout-${Date.now()}`,
      name,
      tabs: {
        'key-metrics': {
          widgets: generateDefaultWidgets('key-metrics'),
          layouts: generateDefaultWidgets('key-metrics').map(widget => widget.layout as GridItemLayout),
        },
        'call-overview': {
          widgets: generateDefaultWidgets('call-overview'),
          layouts: generateDefaultWidgets('call-overview').map(widget => widget.layout as GridItemLayout),
        },
        'agent-performance': {
          widgets: generateDefaultWidgets('agent-performance'),
          layouts: generateDefaultWidgets('agent-performance').map(widget => widget.layout as GridItemLayout),
        },
        'trends-insights': {
          widgets: generateDefaultWidgets('trends-insights'),
          layouts: generateDefaultWidgets('trends-insights').map(widget => widget.layout as GridItemLayout),
        }
      }
    };

    setLayouts(prev => [...prev, newLayout]);
    setCurrentLayout(newLayout);
  };

  const setCurrent = (id: number) => {
    const dashboards = GetDashboards();
    const dashboard = dashboards.find(d => d.id === id);
    if (dashboard) {
      setCurrentState(dashboard);
    }
  };

  const switchLayout = async (layoutId: string) => {
    const layout = layouts.find(l => l.id === layoutId);
    if (layout) {
      setCurrentLayout(layout);
    }
  };

  const deleteLayout = (layoutId: string) => {
    if (layouts.length === 1) {
      toast.error("Cannot delete the last layout");
      return;
    }

    setLayouts(prev => prev.filter(layout => layout.id !== layoutId));
    if (currentLayout.id === layoutId) {
      setCurrentLayout(layouts[0]);
    }
  };

  const openWidgetSettings = (widgetId: string) => {
    const currentTab = activeTab as keyof typeof currentLayout.tabs;
    const widget = currentLayout.tabs[currentTab].widgets.find(w => w.id === widgetId);
    if (widget) {
      setCurrentWidgetSettings(widget);
    }
  };

  const closeWidgetSettings = () => {
    setCurrentWidgetSettings(null);
  };

  // Combine current layout with temporary widget values for display
  const effectiveCurrentLayout = {
    ...currentLayout,
    tabs: {
      ...currentLayout.tabs,
      [activeTab as keyof typeof currentLayout.tabs]: {
        ...currentLayout.tabs[activeTab as keyof typeof currentLayout.tabs],
        widgets: currentLayout.tabs[activeTab as keyof typeof currentLayout.tabs].widgets.map(widget => {
          if (tempWidgetValues[widget.id]) {
            return tempWidgetValues[widget.id];
          }
          return widget;
        })
      }
    }
  };

  return (
    <DashboardContext.Provider
      value={{
        layouts,
        currentLayout: effectiveCurrentLayout,
        updateLayout,
        addWidget,
        removeWidget,
        updateWidget,
        updateWidgetTemp,
        resetWidgetTemp,
        saveDashboard,
        resetDashboard,
        openWidgetSettings,
        closeWidgetSettings,
        currentWidgetSettings,
        createNewLayout,
        switchLayout,
        deleteLayout,
        theme,
        setTheme,
        isGeneralMode,
        toggleGeneralMode,
        setIsGeneralMode,
        activeTab,
        setActiveTab,
        current,
        setCurrent,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboardData = () => {
  const { current, setCurrent } = useDashboard();
  return { current, setCurrent };
};
